/**
 * This is the entry point for Feature Hub App integration
 */

import React from 'react';
import type {
  FeatureAppDefinition,
  FeatureAppEnvironment,
  FeatureServices,
} from '@feature-hub/core';
import type { ReactFeatureApp } from '@feature-hub/react';

import type {
  GfaLocaleService,
  GfaServiceConfigProvider,
  NotificationDisplayService,
  TrackingService,
} from './feature-service-types';

import FeatureApp from './FeatureApp';
import { getDesignLibrary } from '../alpha-helper';

export interface FeatureServiceDependencies extends FeatureServices {
  readonly 'gfa:locale-service': GfaLocaleService;
  readonly 'gfa:service-config-provider': GfaServiceConfigProvider;
  readonly 'notification-display-service': NotificationDisplayService;
  readonly 'audi-tracking-service'?: TrackingService;
}

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, FeatureServiceDependencies> = {
  dependencies: {
    featureServices: {
      'gfa:locale-service': '^1.0.0',
      'gfa:service-config-provider': '^1.0.0',
    },
    externals: {
      react: '*',
      'styled-components': '*',
      ...getDesignLibrary({ unifiedWebCommonVersion: '^1.0.0', unifiedWebAlphaCommonVersion: '*' }),
    },
  },
  optionalDependencies: {
    featureServices: {
      'audi-tracking-service': '^2.0.0',
      'notification-display-service': '^1.0.0',
    },
  },

  create: ({ featureServices }: FeatureAppEnvironment<FeatureServiceDependencies, void>) => {
    const localeService = featureServices['gfa:locale-service'];
    const notificationDisplayService = featureServices['notification-display-service'];
    const serviceConfigProvider = featureServices['gfa:service-config-provider'];

    const trackingService = featureServices['audi-tracking-service'];
    if (trackingService) {
      trackingService.featureAppName = __FEATURE_APP_NAME__;
    }

    return {
      render: () => {
        return (
          <FeatureApp
            localeService={localeService}
            serviceConfigProvider={serviceConfigProvider}
            notificationDisplayService={notificationDisplayService}
            trackingService={trackingService}
          />
        );
      },
    };
  },
};

export default featureAppDefinition;
